import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import propertiesService from '../services/propertiesService';
import styles from '../assets/documentPreviewModal.module.css';

// PDF Iframe Viewer Component - Fetches PDF with auth and displays using blob URL
const PdfIframeViewer = ({ documentId, documentName }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

        console.log('🔍 Fetching PDF with authentication...');
        console.log('  - Document ID:', documentId);
        console.log('  - API URL:', apiUrl);
        console.log('  - Token exists:', !!token);

        // Get presigned URL from backend for direct S3 access
        const response = await fetch(`${apiUrl}/api/documents/show/${documentId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('📥 Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch PDF: ${response.status} - ${errorText}`);
        }

        const result = await response.json();

        if (!result.success || !result.data.presignedUrl) {
          throw new Error(result.message || "Failed to get presigned URL");
        }

        // Use presigned URL directly for iframe (matching Laravel's direct S3 access)
        const presignedUrl = result.data.presignedUrl;
        console.log('✅ Presigned URL received for PDF preview');
        setBlobUrl(presignedUrl);
        console.log('✅ Using presigned URL for iframe:', presignedUrl);

        setLoading(false);
      } catch (err) {
        console.error('❌ Error fetching PDF:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    if (documentId) {
      fetchPdf();
    }

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [documentId]);

  if (loading) {
    return (
      <div className={styles.pdfPreview}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '600px' }}>
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading PDF...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pdfPreview}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '600px' }}>
          <i className="glyphicon glyphicon-exclamation-sign" style={{ fontSize: '48px', color: '#d9534f', marginBottom: '20px' }}></i>
          <p>Error loading PDF: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pdfPreview}>
      <iframe
        src={blobUrl}
        className={styles.pdfViewer}
        title={documentName}
        onLoad={() => console.log('✅ PDF iframe loaded successfully')}
      />
    </div>
  );
};

const DocumentPreviewModal = ({ isOpen, onClose, propertyId, documentId, documentName, documentType }) => {
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && documentId) {
      loadDocumentData();
    }
  }, [isOpen, documentId]);

  const loadDocumentData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get document details from the property documents API
      const response = await propertiesService.getPropertyDocument(propertyId, documentId);
      
      if (response.success) {
        console.log('📄 Document data received:', response.data);
        console.log('🔑 Document key:', response.data.document_key);
        console.log('🔑 Document key type:', typeof response.data.document_key);
        console.log('🔑 Document key length:', response.data.document_key?.length);
        console.log('🌐 S3 CDN Name:', process.env.REACT_APP_S3_CDN_NAME);
        console.log('🔗 Constructed S3 URL:', getS3Url(response.data.document_key));
        console.log('📁 File name:', response.data.name);
        console.log('📁 File type:', response.data.file_type);
        setDocumentData(response.data);
      } else {
        setError(response.message || 'Failed to load document');
      }
    } catch (error) {
      console.error('Error loading document:', error);
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await propertiesService.downloadPropertyDocument(propertyId, documentId);
      
      if (response.success) {
        toast.success('Document downloaded successfully');
      } else {
        toast.error(response.message || 'Failed to download document');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return '/images/pdf.png';
    
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
        return '/images/pdf.png';
      case 'doc':
      case 'docx':
        return '/images/doc.png';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '/images/image.png';
      default:
        return '/images/file.png';
    }
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension);
  };

  const isPdfFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().split('.').pop();
    return extension === 'pdf';
  };

  // ✅ FIXED: Use presigned URL from backend instead of constructing CDN URL
  // Backend now returns file_url as presigned URL in getPropertyDocument response
  const getS3Url = (documentData) => {
    if (!documentData) return null;
    
    // Use presigned URL from backend if available (preferred)
    if (documentData.file_url && documentData.file_url.startsWith('http')) {
      return documentData.file_url;
    }
    
    // Fallback to document_key if file_url not available
    if (documentData.document_key) {
      // If document_key is already a full URL, use it
      if (documentData.document_key.startsWith('http')) {
        return documentData.document_key;
      }
      
      // Otherwise, try to construct CDN URL as last resort
      const cdnName = process.env.REACT_APP_S3_CDN_NAME;
      if (cdnName) {
        const cleanCdnName = cdnName.replace(/\/$/, '');
        return `https://${cleanCdnName}/${documentData.document_key}`;
      }
    }
    
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <button className={styles.closeButton} onClick={onClose}>
            <i className="glyphicon glyphicon-remove"></i>
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p>Loading document...</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <div className="alert alert-danger">
                <i className="glyphicon glyphicon-exclamation-sign"></i>
                <strong>Error:</strong> {error}
              </div>
            </div>
          ) : documentData ? (
            <div className={styles.documentPreview}>
              {/* Document Information - Hidden */}

              {/* Document Preview */}
              <div className={styles.previewContainer}>
                {isImageFile(documentData.file_name || documentData.document_key || documentData.name) ? (
                  <div className={styles.imagePreview}>
                    <img 
                      src={getS3Url(documentData) || documentData.file_url || documentData.file_path} 
                      alt={documentName}
                      className={styles.previewImage}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className={styles.previewFallback} style={{ display: 'none' }}>
                      <i className="glyphicon glyphicon-picture"></i>
                      <p>Image preview not available</p>
                    </div>
                  </div>
                ) : isPdfFile(documentData.file_name || documentData.document_key || documentData.name) ? (
                  <PdfIframeViewer documentId={documentId} documentName={documentName} />

                ) : (
                  <div className={styles.filePreview}>
                    <div className={styles.fileIconLarge}>
                      <img src={getFileIcon(documentData.file_name)} alt="File" />
                    </div>
                    <p>Preview not available for this file type</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.noDataContainer}>
              <i className="glyphicon glyphicon-file"></i>
              <p>No document data available</p>
            </div>
          )}
        </div>
        
        {/* Modal Footer - Hidden */}
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
